import React from 'react';
import loadable from '@loadable/component';
import { LANG_URL_PARAM } from '../constants/locales';
import getPageDataLoadingAPI from '../containers/pageDataLoadingAPI';
import NotFoundPage from '../containers/NotFoundPage/NotFoundPage';
import PreviewResolverPage from '../containers/PreviewResolverPage/PreviewResolverPage';

// routeConfiguration needs to initialize containers first
// Otherwise, components will import form container eventually and
// at that point css bundling / imports will happen in wrong order.
import { NamedRedirect } from '../components';

const pageDataLoadingAPI = getPageDataLoadingAPI();

const ContactPage = loadable(() => import(/* webpackChunkName: "ContactPage" */ '../containers/ContactPage/ContactPage'));
const AuthenticationPage = loadable(() => import(/* webpackChunkName: "AuthenticationPage" */ '../containers/AuthenticationPage/AuthenticationPage'));
const CheckoutPage = loadable(() => import(/* webpackChunkName: "CheckoutPage" */ '../containers/CheckoutPage/CheckoutPage'));
const ContactDetailsPage = loadable(() => import(/* webpackChunkName: "ContactDetailsPage" */ '../containers/ContactDetailsPage/ContactDetailsPage'));
const EditListingPage = loadable(() => import(/* webpackChunkName: "EditListingPage" */ '../containers/EditListingPage/EditListingPage'));
const EmailVerificationPage = loadable(() => import(/* webpackChunkName: "EmailVerificationPage" */ '../containers/EmailVerificationPage/EmailVerificationPage'));
const InboxPage = loadable(() => import(/* webpackChunkName: "InboxPage" */ '../containers/InboxPage/InboxPage'));
const MessagesPage = loadable(() => import(/* webpackChunkName: "MessagesPage" */ '../containers/MessagesPage/MessagesPage'));
const LandingPage = loadable(() => import(/* webpackChunkName: "LandingPage" */ '../containers/LandingPage/LandingPage'));
const CrionAIPage = loadable(() => import(/* webpackChunkName: "CrionAIPage" */ '../containers/CrionAIPage/CrionAIPage'));
const ListingPage = loadable(() => import(/* webpackChunkName: "ListingPage" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPage'));
const PasswordChangePage = loadable(() => import(/* webpackChunkName: "PasswordChangePage" */ '../containers/PasswordChangePage/PasswordChangePage'));
const PasswordRecoveryPage = loadable(() => import(/* webpackChunkName: "PasswordRecoveryPage" */ '../containers/PasswordRecoveryPage/PasswordRecoveryPage'));
const PasswordResetPage = loadable(() => import(/* webpackChunkName: "PasswordResetPage" */ '../containers/PasswordResetPage/PasswordResetPage'));
const PaymentMethodsPage = loadable(() => import(/* webpackChunkName: "PaymentMethodsPage" */ '../containers/PaymentMethodsPage/PaymentMethodsPage'));
const CalendarPage = loadable(() => import(/* webpackChunkName: "CalendarPage" */ '../containers/CalendarPage/CalendarPage'));
const PrivacyPolicyPage = loadable(() => import(/* webpackChunkName: "PrivacyPolicyPage" */ '../containers/PrivacyPolicyPage/PrivacyPolicyPage'));
const ProfilePage = loadable(() => import(/* webpackChunkName: "ProfilePage" */ '../containers/ProfilePage/ProfilePage'));
const ProfileSettingsPage = loadable(() => import(/* webpackChunkName: "ProfileSettingsPage" */ '../containers/ProfileSettingsPage/ProfileSettingsPage'));
const SearchPage = loadable(() => import(/* webpackChunkName: "SearchPage" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPage'));
const StripePayoutPage = loadable(() => import(/* webpackChunkName: "StripePayoutPage" */ '../containers/StripePayoutPage/StripePayoutPage'));
const PromotionsPage = loadable(() => import(/* webpackChunkName: "PromotionsPage" */ '../containers/PromotionsPage/PromotionsPage'));
const TermsAndConditionsPage = loadable(() => import(/* webpackChunkName: "TermsAndConditionsPage" */ '../containers/TermsAndConditionsPage/TermsAndConditionsPage'));
const TransactionPage = loadable(() => import(/* webpackChunkName: "TransactionPage" */ '../containers/TransactionPage/TransactionPage'));
const ManageListingsPage = loadable(() => import(/* webpackChunkName: "ManageListingsPage" */ '../containers/ManageListingsPage/ManageListingsPage'));
const HelpCenterBasePage = loadable(() => import(/* webpackChunkName: "HelpCenterBasePage" */ '../containers/HelpCenter/HelpCenterBasePage'));
const HelpCenterPage = loadable(() => import(/* webpackChunkName: "HelpCenterPage" */ '../containers/HelpCenter/HelpCenterPage'));
const SeoPage = loadable(() => import(/* webpackChunkName: "SeoPage" */ '../containers/SeoPage/SeoPage'));
const AdminVouchersPage = loadable(() => import(/* webpackChunkName: "AdminVouchersPage" */ '../containers/AdminVouchersPage/AdminVouchersPage'));
const AdminAssignmentsPage = loadable(() => import(/* webpackChunkName: "AdminAssignmentsPage" */ '../containers/AdminAssignmentsPage/AdminAssignmentsPage'));
const GiftCardPage = loadable(() => import(/* webpackChunkName: "GiftCardPage" */ '../containers/GiftCardPage/GiftCardPage'));
const WelfarePage = loadable(() => import(/* webpackChunkName: "WelfarePage" */ '../containers/WelfarePage/WelfarePage'));
const CompanyDashboardPage = loadable(() => import(/* webpackChunkName: "CompanyDashboardPage" */ '../containers/CompanyDashboardPage/CompanyDashboardPage'));
const WelfareIndexPage = loadable(() => import(/* webpackChunkName: "WelfareIndexPage" */ '../containers/WelfareIndexPage/WelfareIndexPage'));
const AccountWelfarePage = loadable(() => import(/* webpackChunkName: "AccountWelfarePage" */ '../containers/AccountWelfarePage/AccountWelfarePage'));
const PartnerLandingPage = loadable(() => import(/* webpackChunkName: "PartnerLandingPage" */ '../containers/PartnerLandingPage/PartnerLandingPage'));

export const ACCOUNT_SETTINGS_PAGES = [
  'ContactDetailsPage',
  'PasswordChangePage',
  'StripePayoutPage',
  'PaymentMethodsPage',
  'ProfileSettingsPage',
  'CalendarPage',
  'PromotionsPage',
];

// https://en.wikipedia.org/wiki/Universally_unique_identifier#Nil_UUID
const draftId = '00000000-0000-0000-0000-000000000000';
const draftSlug = 'draft';

const RedirectToLandingPage = () => <NamedRedirect name="LandingPage" />;

// NOTE: Most server-side endpoints are prefixed with /api. Requests to those
// endpoints are intended to be handled in the server instead of the browser and
// they will not render the application. So remember to avoid routes starting
// with /api and if you encounter clashing routes see server/index.js if there's
// a conflicting route defined there.

// Our routes are exact by default.
// See behaviour from Routes.js where Route is created.
const routeConfiguration = (layoutConfig) => {


  const routes = [
    {
      path: '/',
      name: 'LandingPage',
      component: LandingPage,
      loadData: pageDataLoadingAPI.LandingPage.loadData,
    },
    {
      path: '/crion-ai',
      name: 'CrionAIPage',
      component: CrionAIPage,
    },
    {
      path: '/contact',
      name: 'ContactPage',
      component: ContactPage,
    },
    {
      path: '/gift-card',
      name: 'GiftCardPage',
      component: GiftCardPage,
    },
    {
      path: '/help',
      name: 'HelpCenterBasePage',
      component: HelpCenterBasePage,
    },
    {
      path: '/help/:tab/:subtab',
      name: 'HelpCenterPage',
      component: HelpCenterPage,
    },
    {
      path: '/:country?/:region?/:discipline?/s',
      name: 'SearchPage',
      component: SearchPage,
      loadData: pageDataLoadingAPI.SearchPage.loadData,
    },
    {
      path: '/listings',
      name: 'ManageListingsBasePage',
      component: () => <NamedRedirect name="ManageListingsPage" params={{ tab: 'published' }} />,
    },
    {
      path: '/listings/:tab',
      name: 'ManageListingsPage',
      component: ManageListingsPage,
    },
    {
      path: '/l/:slug/:id/checkout',
      name: 'CheckoutPage',
      auth: true,
      component: CheckoutPage,
      loadData: pageDataLoadingAPI.CheckoutPage.loadData,
      setInitialValues: pageDataLoadingAPI.CheckoutPage.setInitialValues,
    },
    {
      path: '/l/:slug/:id/:variant',
      name: 'ListingPageVariant',
      auth: true,
      authPage: 'LoginPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/new',
      name: 'NewListingPage',
      auth: true,
      component: () => (
        <NamedRedirect
          name="EditListingPage"
          params={{ slug: draftSlug, id: draftId, type: 'new', tab: 'activity' }}
        />
      ),
    },
    {
      path: '/l/:slug/:id/:type/:tab',
      name: 'EditListingPage',
      auth: true,
      authPage: 'LoginPage',
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/:type/:tab/:returnURLType',
      name: 'EditListingStripeOnboardingPage',
      auth: true,
      authPage: 'LoginPage',
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },
    {
      path: '/u',
      name: 'ProfileBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/u/:id',
      name: 'ProfilePage',
      component: ProfilePage,
      loadData: pageDataLoadingAPI.ProfilePage.loadData,
    },
    // Note: authenticating with IdP (e.g. Facebook) expects that /login path exists
    // so that in the error case users can be redirected back to the LoginPage
    // In case you change this, remember to update the route in server/api/auth/loginWithIdp.js
    {
      path: '/login',
      name: 'LoginPage',
      component: AuthenticationPage,
      extraProps: { tab: 'login' },
    },
    {
      path: '/signup',
      name: 'SignupPage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/confirm',
      name: 'ConfirmPage',
      component: AuthenticationPage,
      extraProps: { tab: 'confirm' },
    },
    {
      path: '/recover-password',
      name: 'PasswordRecoveryPage',
      component: PasswordRecoveryPage,
    },
    {
      path: '/bookings',
      name: 'InboxBasePage',
      auth: true,
      authPage: 'LoginPage',
      component: () => (
        <NamedRedirect name="InboxPage" params={{ tab: 'sales', type: 'upcoming' }} />
      ),
    },
    {
      path: '/bookings/orders',
      name: 'InboxBaseOrdersPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => (
        <NamedRedirect name="InboxPage" params={{ tab: 'orders', type: 'upcoming' }} />
      ),
    },
    {
      path: '/bookings/sales',
      name: 'InboxBaseSalesPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => (
        <NamedRedirect name="InboxPage" params={{ tab: 'sales', type: 'upcoming' }} />
      ),
    },
    {
      path: '/bookings/:tab/:type',
      name: 'InboxPage',
      auth: true,
      authPage: 'LoginPage',
      component: InboxPage,
      loadData: pageDataLoadingAPI.InboxPage.loadData,
    },
    {
      path: '/messages',
      name: 'MessagesPage',
      auth: true,
      authPage: 'LoginPage',
      component: MessagesPage,
      loadData: pageDataLoadingAPI.MessagesPage.loadData,
    },
    {
      path: '/order/:id',
      name: 'OrderDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'customer' },
      loadData: (params, ...rest) =>
        pageDataLoadingAPI.TransactionPage.loadData(
          { ...params, transactionRole: 'customer' },
          ...rest
        ),
      setInitialValues: pageDataLoadingAPI.TransactionPage.setInitialValues,
    },
    {
      path: '/order/:id/details',
      name: 'OrderDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => (
        <NamedRedirect name="OrderDetailsPage" params={{ id: props.params?.id }} />
      ),
    },
    {
      path: '/sale/:id',
      name: 'SaleDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'provider' },
      loadData: pageDataLoadingAPI.TransactionPage.loadData,
    },
    {
      path: '/sale/:id/details',
      name: 'SaleDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => (
        <NamedRedirect name="SaleDetailsPage" params={{ id: props.params?.id }} />
      ),
    },
    {
      path: '/account',
      name: 'AccountSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="ContactDetailsPage" />,
    },
    {
      path: '/account/contact-details',
      name: 'ContactDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ContactDetailsPage,
      loadData: pageDataLoadingAPI.ContactDetailsPage.loadData,
    },
    {
      path: '/account/change-password',
      name: 'PasswordChangePage',
      auth: true,
      authPage: 'LoginPage',
      component: PasswordChangePage,
    },
    {
      path: '/account/get-paid',
      name: 'StripePayoutPage',
      auth: true,
      authPage: 'LoginPage',
      component: StripePayoutPage,
      loadData: pageDataLoadingAPI.StripePayoutPage.loadData,
    },
    {
      path: '/account/get-paid/:returnURLType',
      name: 'StripePayoutOnboardingPage',
      auth: true,
      authPage: 'LoginPage',
      component: StripePayoutPage,
      loadData: pageDataLoadingAPI.StripePayoutPage.loadData,
    },
    {
      path: '/account/payment-methods',
      name: 'PaymentMethodsPage',
      auth: true,
      authPage: 'LoginPage',
      component: PaymentMethodsPage,
      loadData: pageDataLoadingAPI.PaymentMethodsPage.loadData,
    },
    {
      path: '/account/calendar',
      name: 'CalendarPage',
      auth: true,
      authPage: 'LoginPage',
      component: CalendarPage,
      loadData: pageDataLoadingAPI.CalendarPage.loadData,
    },
    {
      path: '/account/promotions',
      name: 'PromotionsPage',
      auth: true,
      authPage: 'LoginPage',
      component: PromotionsPage,
      loadData: pageDataLoadingAPI.PromotionsPage.loadData,
    },
    {
      path: '/account/profile-settings',
      name: 'ProfileSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ProfileSettingsPage,
    },
    {
      path: '/admin/vouchers',
      name: 'AdminVouchersPage',
      auth: true,
      authPage: 'LoginPage',
      component: AdminVouchersPage,
      loadData: pageDataLoadingAPI.AdminVouchersPage.loadData,
    },
    {
      path: '/admin/assignments',
      name: 'AdminAssignmentsPage',
      auth: true,
      authPage: 'LoginPage',
      component: AdminAssignmentsPage,
      loadData: pageDataLoadingAPI.AdminAssignmentsPage.loadData,
    },
    {
      path: '/corporate/welfare',
      name: 'WelfareIndexPage',
      component: WelfareIndexPage,
      loadData: pageDataLoadingAPI.WelfareIndexPage.loadData,
    },
    {
      path: '/corporate/dashboard',
      name: 'CorporateDashboardPage',
      auth: true,
      authPage: 'LoginPage',
      component: AccountWelfarePage,
      loadData: pageDataLoadingAPI.AccountWelfarePage.loadData,
    },
    {
      path: '/corporate/:slug',
      name: 'WelfarePage',
      component: WelfarePage,
      loadData: pageDataLoadingAPI.WelfarePage.loadData,
    },
    {
      path: '/partner',
      name: 'PartnerLandingPage',
      component: PartnerLandingPage,
      loadData: pageDataLoadingAPI.PartnerLandingPage.loadData,
    },
    {
      path: '/terms-and-conditions',
      name: 'TermsAndConditionsPage',
      component: TermsAndConditionsPage,
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicyPage',
      component: PrivacyPolicyPage,
      loadData: pageDataLoadingAPI.PrivacyPolicyPage.loadData,
    },
    {
      path: '/notfound',
      name: 'NotFoundPage',
      component: props => <NotFoundPage {...props} />,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /reset-password endpoint
    {
      path: '/reset-password',
      name: 'PasswordResetPage',
      component: PasswordResetPage,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /verify-email endpoint
    {
      path: '/verify-email',
      name: 'EmailVerificationPage',
      auth: true,
      authPage: 'LoginPage',
      component: EmailVerificationPage,
      loadData: pageDataLoadingAPI.EmailVerificationPage.loadData,
    },
    // Do not change this path!
    //
    // The API expects that the application implements /preview endpoint
    {
      path: '/preview',
      name: 'PreviewResolverPage',
      component: PreviewResolverPage,
    },

    {
      path: '/outdoor-activities',
      name: 'SeoPageOaEn',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    {
      path: '/outdoor-activities/:slug',
      name: 'SeoPageOaEn',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },

    // DE
    {
      path: '/outdoor-aktivitaeten',
      name: 'SeoPageOaDe',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    {
      path: '/outdoor-aktivitaeten/:slug',
      name: 'SeoPageOaDe',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    //

    // FR
    {
      path: '/activites-outdoor',
      name: 'SeoPageOaFr',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    {
      path: '/activites-outdoor/:slug',
      name: 'SeoPageOaFr',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    //

    // IT
    {
      path: '/attivita-outdoor',
      name: 'SeoPageOaIt',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    {
      path: '/attivita-outdoor/:slug',
      name: 'SeoPageOaIt',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    //

    {
      path: '/outdoor-destinations',
      name: 'SeoPageOdEn',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    {
      path: '/outdoor-destinations/:slug',
      name: 'SeoPageOdEn',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    {
      path: '/outdoor-destinations/:country/:slug',
      name: 'SeoPageOdEn',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    {
      path: '/outdoor-destinations/:country/:region/:slug',
      name: 'SeoPageOdEn',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },

    //// DE
    {
      path: '/outdoor-ziele',
      name: 'SeoPageOdDe',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    {
      path: '/outdoor-ziele/:slug',
      name: 'SeoPageOdDe',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    {
      path: '/outdoor-ziele/:country/:slug',
      name: 'SeoPageOdDe',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    {
      path: '/outdoor-ziele/:country/:region/:slug',
      name: 'SeoPageOdDe',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    ////

    //// FR
    {
      path: '/destinations-outdoor',
      name: 'SeoPageOdFe',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    {
      path: '/destinations-outdoor/:slug',
      name: 'SeoPageOdFe',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    {
      path: '/destinations-outdoor/:country/:slug',
      name: 'SeoPageOdFe',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    {
      path: '/destinations-outdoor/:country/:region/:slug',
      name: 'SeoPageOdFe',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    ////

    //// IT
    {
      path: '/destinazioni-outdoor',
      name: 'SeoPageOdIt',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    {
      path: '/destinazioni-outdoor/:slug',
      name: 'SeoPageOdIt',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    {
      path: '/destinazioni-outdoor/:country/:slug',
      name: 'SeoPageOdIt',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    {
      path: '/destinazioni-outdoor/:country/:region/:slug',
      name: 'SeoPageOdIt',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    ////

    {
      path: '/outdoor-stories',
      name: 'SeoPageOsEn',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    {
      path: '/outdoor-stories/:slug',
      name: 'SeoPageOsEn',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    {
      path: '/outdoor-stories/:articletype/:slug',
      name: 'SeoPageOsEn',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },

    /// DE
    {
      path: '/outdoor-geschichten',
      name: 'SeoPageOsDe',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    {
      path: '/outdoor-geschichten/:slug',
      name: 'SeoPageOsDe',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    {
      path: '/outdoor-geschichten/:articletype/:slug',
      name: 'SeoPageOsDe',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    ///

    /// FR
    {
      path: '/histoires-outdoor',
      name: 'SeoPageOsFr',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    {
      path: '/histoires-outdoor/:slug',
      name: 'SeoPageOsFr',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    {
      path: '/histoires-outdoor/:articletype/:slug',
      name: 'SeoPageOsFr',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    ///

    /// IT
    {
      path: '/storie-outdoor',
      name: 'SeoPageOsIt',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    {
      path: '/storie-outdoor/:slug',
      name: 'SeoPageOsIt',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },
    {
      path: '/storie-outdoor/:articletype/:slug',
      name: 'SeoPageOsIt',
      component: SeoPage,
      loadData: pageDataLoadingAPI.SeoPage.loadData,
    },

    // Listing Page
    {
      path: '/:type/:slug/:id',
      name: 'ListingPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },

  ];

  const { partnerPagesMapping } = layoutConfig || {};

  let sanitizedRoutes = routes;

  if (partnerPagesMapping) {
    sanitizedRoutes = routes.map(route => {

      const routeDestination = partnerPagesMapping[route.name];

      if (!routeDestination) return route;

      const { type, name } = routeDestination;

      const foundRoute = routes.find(r => r.name === name);

      if (!foundRoute) return route;

      switch (type) {
        case 'swap':
          return { ...foundRoute, path: route.path, name: route.name };

        case 'redirect':
          return { ...route, component: () => <NamedRedirect name={name} /> };

        default:
          break;
      }

      return { ...foundRoute, path: route.path, name: route.name };
    })
  }

  return sanitizedRoutes.map(route => {
    const routePath = route.path === '/' ? '' : route.path

    return { ...route, path: `/${LANG_URL_PARAM}${routePath}` };
  }
  );;
};

export default routeConfiguration;
